import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  Science,
  NavigateBefore,
  NavigateNext,
  PhotoCamera,
  CalendarToday,
  ExpandMore,
  LocalDrink,
  Grain,
  WaterDrop,
  Bloodtype,
  Whatshot,
  AcUnit,
  PanTool,
  Healing,
  CheckCircle,
  ErrorOutline,
  Send,
} from "@mui/icons-material";
import { useDashboard } from "../../../contexts/DashboardContext";
import CowTeatPicker from "./CowTeatPicker";
import { MastitisTestService, MastitisTestData } from "../services/MastitisTestService";
import { getTreatmentByOrganism, AINES_RECOMENDADOS } from "../services/mastitisTreatment";
import { useChatContext } from "../../agent/contexts/ChatContext";
import { useAuth } from "../../../hooks/useAuth";
import { storageService } from "../../../services/storageService";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import infoMastitis from "../../../../info-mastitis.json";

type Teat = "AE" | "AD" | "PE" | "PD";
type CategoryKey = "ImagemCromogenicoStaphylococcus" | "ImagemCromogenicoGeral" | "ImagemCromogenicoStreptococcus";

const CATEGORY_FLOW: { key: CategoryKey; title: string; hint: string }[] = [
  { key: "ImagemCromogenicoStaphylococcus", title: "Cromogênico Staphylococcus", hint: "Selecione a imagem mais próxima do seu resultado." },
  { key: "ImagemCromogenicoStreptococcus", title: "Cromogênico Streptococcus", hint: "Selecione a imagem mais compatível com Streptococcus." },
  { key: "ImagemCromogenicoGeral", title: "Cromogênico Geral", hint: "Selecione a imagem correspondente ao resultado geral." },
];

const tutorialSteps = [
  {
    id: 1,
    title: "Responsabilidade e Qualificação",
    description: "Este procedimento deve ser executado por médico veterinário, técnico ou analista treinado em coleta e manipulação de amostras clínicas.",
    tip: "Importante: O produtor pode executar este procedimento desde que tenha recebido treinamento adequado e conte com auxílio técnico especializado. A qualificação é essencial para garantir a qualidade dos resultados.",
  },
  {
    id: 2,
    title: "Preparação do Animal",
    description: "Identifique corretamente o animal e o teto a ser coletado antes de iniciar o procedimento.",
    tip: "Identificação: Anote o número/nome do animal e especifique qual teto será coletado: Dianteiro Direito (DD), Dianteiro Esquerdo (DE), Traseiro Direito (TD) ou Traseiro Esquerdo (TE). Esta informação será crucial para o diagnóstico.",
  },
  {
    id: 3,
    title: "Higienização do Teto",
    description: "Realize a desinfecção adequada do teto seguindo a sequência correta.",
    tip: "Procedimento: Utilize solução desinfetante nos tetos, sempre do mais distante ao mais próximo. Deixe secar ao ar livre (nunca sopre ou toque). Se necessário, seque com papel toalha estéril. A higienização adequada é fundamental para evitar contaminações.",
  },
  {
    id: 4,
    title: "Descarte do Primeiro Jato",
    description: "Despreze os três primeiros jatos de leite de cada teto antes da coleta.",
    tip: "Importante: Este procedimento remove possíveis contaminações do canal do teto e garante que a amostra seja representativa do leite da glândula mamária. Nunca colete os primeiros jatos.",
  },
  {
    id: 5,
    title: "Coleta da Amostra",
    description: "Colete aproximadamente 10 mL de leite diretamente em frasco estéril.",
    tip: "Procedimento: Colete o leite diretamente no frasco estéril, evitando contato com superfícies externas. Feche imediatamente após a coleta. Identifique com: número do animal, teto coletado, data e hora da coleta.",
  },
  {
    id: 6,
    title: "Transporte da Amostra",
    description: "Mantenha as amostras refrigeradas durante o transporte até o laboratório.",
    tip: "Conservação: Mantenha entre 2°C e 8°C em caixa térmica. NUNCA congele, pois pode causar lise bacteriana. O tempo máximo entre coleta e semeadura deve ser de no máximo 12 horas para garantir a viabilidade dos microrganismos.",
  },
  {
    id: 7,
    title: "Recebimento no Laboratório",
    description: "Verifique a identificação e integridade da amostra antes do processamento.",
    tip: "Verificação: Confirme se a identificação está legível e completa. Verifique se o frasco está íntegro e sem vazamentos. Homogeneize suavemente por inversão antes de abrir (não agite vigorosamente).",
  },
  {
    id: 8,
    title: "Preparação para Semeadura",
    description: "Prepare adequadamente a placa e a amostra antes da inoculação.",
    tip: "Preparação: Deixe a placa de Agar Cromast dentro da estufa por pelo menos 1 hora para secar o líquido evaporado. Deixe a amostra de leite atingir temperatura ambiente antes da semeadura. Esta etapa é crucial para o sucesso do cultivo.",
  },
  {
    id: 9,
    title: "Técnica de Semeadura",
    description: "Realize a inoculação da amostra no Agar Cromast seguindo técnica asséptica.",
    tip: "Procedimento: Use técnica asséptica em bancada limpa ou cabine de segurança biológica. Com alça estéril, inocule uma alíquota de leite no Agar Cromast. Espalhe em estrias sucessivas para isolamento de colônias. Mantenha esterilidade durante todo o processo.",
  },
  {
    id: 10,
    title: "Incubação e Leitura",
    description: "Incube as placas nas condições adequadas e realize a leitura dos resultados.",
    tip: "Condições: Incube a 35-37°C por 44-48 horas. Após este período, observe o crescimento de colônias, suas características morfológicas e coloração. O Agar Cromast permite identificação presuntiva dos microrganismos pela cor das colônias.",
  },
];

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return "-";
  try {
    const [y, m, d] = dateStr.split("T")[0].split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return dateStr;
  }
};

const normalizeAttachments = (attachments: any) => {
  try {
    if (!attachments) return [];
    if (Array.isArray(attachments)) return attachments;
    if (typeof attachments === "string") {
      const parsed = JSON.parse(attachments);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") return [parsed];
    }
    if (typeof attachments === "object") return [attachments];
    return [];
  } catch {
    return [];
  }
};

const MobileExamsHub: React.FC = () => {
  const { updateContent, restorePreviousContent } = useDashboard();
  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Science color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Exames</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Selecione um exame para começar.
      </Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Science color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Análise de Mastite</Typography>
          <Chip size="small" color="success" label="Disponível" sx={{ ml: "auto" }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Utilize imagem da placa de cultura e selecione colônias para obter uma análise assistida.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
          <Button variant="contained" onClick={() => updateContent("mastitisIntro")}>
            Iniciar
          </Button>
          <Button variant="outlined" onClick={restorePreviousContent}>
            Sair
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const MobileMastitisIntro: React.FC = () => {
  const { updateContent, restorePreviousContent } = useDashboard();
  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Science color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Teste de Mastite</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Siga as orientações abaixo antes de iniciar o teste para garantir resultados confiáveis.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {tutorialSteps.map((step) => (
          <Accordion key={step.id} elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {step.id}. {step.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {step.description}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {step.tip}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
        <Button variant="outlined" onClick={restorePreviousContent}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => updateContent("mastitisTeatStep")}>
          Começar teste
        </Button>
      </Box>
    </Box>
  );
};

const MobileMastitisTeatStep: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [selected, setSelected] = useState<Teat[]>(
    Array.isArray(initialData?.mastitis?.teats) ? (initialData?.mastitis?.teats as Teat[]) : []
  );

  const handleNext = () => {
    const payload = {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        teats: selected,
      },
    };
    updateContent("mastitisAnimalIdStep", payload);
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Seleção de Teto</Typography>
      <Typography variant="body2" color="text.secondary">
        Selecione os tetos com suspeita de mastite.
      </Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box sx={{ width: 260, maxWidth: "100%" }}>
            <CowTeatPicker
              backgroundSrc={"/vaca-pespective.svg"}
              value={selected}
              onChange={setSelected}
              compact
              labels={{ AE: "Teto anterior esquerdo", AD: "Teto anterior direito", PE: "Teto posterior esquerdo", PD: "Teto posterior direito" }}
            />
          </Box>
        </Box>
      </Paper>
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
        <Button variant="outlined" onClick={() => updateContent("mastitisIntro")}>
          Voltar
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={selected.length === 0}>
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const MobileMastitisAnimalIdStep: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [animalId, setAnimalId] = useState<string>(initialData?.mastitis?.animalId || "");

  const handleNext = () => {
    const payload = {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        animalId: animalId?.trim() || null,
      },
    };
    updateContent("mastitisTestStep1", payload);
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Identificação do Animal</Typography>
      <Typography variant="body2" color="text.secondary">
        Informe o número de brinco, nome ou outra referência do animal.
      </Typography>
      <TextField
        fullWidth
        label="Identificação do animal"
        placeholder="Brinco 855, Mimosa"
        value={animalId}
        onChange={(e) => setAnimalId(e.target.value)}
        size="small"
      />
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" onClick={() => updateContent("mastitisTeatStep", initialData)}>
          Voltar
        </Button>
        <Button variant="contained" onClick={handleNext}>
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const MobileMastitisTestStep1: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [selectedFile, setSelectedFile] = useState<File | null>(initialData?.mastitis?.plateImageFile || null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [collectionDate, setCollectionDate] = useState<string | null>(initialData?.mastitis?.collectionDate || null);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleNext = () => {
    if (!selectedFile) return;
    updateContent("mastitisTestStep2", {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        plateImageFile: selectedFile,
        collectionDate,
      },
    });
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Imagem da Placa</Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Exemplo de placa
        </Typography>
        <Box
          component="img"
          src="/mastitis-imgs/example-mastitis.png"
          alt="Exemplo de placa para teste de mastite"
          sx={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 2 }}
        />
      </Paper>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarToday fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Data da coleta</Typography>
          </Box>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={collectionDate || ""}
            onChange={(e) => setCollectionDate(e.target.value || null)}
            InputLabelProps={{ shrink: true }}
          />
          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PhotoCamera fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Enviar imagem</Typography>
          </Box>
          <Button variant="outlined" component="label">
            Selecionar imagem
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
          {previewUrl && (
            <Box sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", p: 1 }}>
              <Box component="img" src={previewUrl} alt="Pré-visualização da imagem enviada" sx={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 1 }} />
            </Box>
          )}
        </Stack>
      </Paper>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" onClick={() => updateContent("mastitisAnimalIdStep", initialData)}>
          Voltar
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={!selectedFile}>
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const MobileMastitisTestStep2: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { updateContent } = useDashboard();
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedPrimary, setSelectedPrimary] = useState<{ [K in CategoryKey]?: string }>({});
  const [selectedSecondary, setSelectedSecondary] = useState<{ [K in CategoryKey]?: string }>({});
  const [prePhase, setPrePhase] = useState(true);
  const [preStepIndex, setPreStepIndex] = useState(0);
  const [milkCondition, setMilkCondition] = useState<string | null>(null);
  const [udderConditions, setUdderConditions] = useState<string[]>([]);

  const results = (infoMastitis as any)?.ResultsMastitis || [];
  const currentCategory = CATEGORY_FLOW[stepIndex];

  const imagesForCurrent = useMemo(() => {
    const set = new Set<string>();
    for (const item of results) {
      const name = item[currentCategory.key];
      if (name && typeof name === "string") {
        set.add(name);
      }
    }
    return Array.from(set);
  }, [results, currentCategory.key]);

  const handleSelect = (filename: string) => {
    const key = currentCategory.key;
    const currentPrimary = selectedPrimary[key];
    const currentSecondary = selectedSecondary[key];

    if (!currentPrimary) {
      setSelectedPrimary((prev) => ({ ...prev, [key]: filename }));
      return;
    }
    if (currentPrimary === filename) {
      setSelectedPrimary((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest as any;
      });
      return;
    }
    if (!currentSecondary) {
      if (filename !== currentPrimary) {
        setSelectedSecondary((prev) => ({ ...prev, [key]: filename }));
      }
      return;
    }
    if (currentSecondary === filename) {
      setSelectedSecondary((prev) => {
        const { [key]: __, ...rest } = prev;
        return rest as any;
      });
      return;
    }
    if (filename !== currentPrimary) {
      setSelectedSecondary((prev) => ({ ...prev, [key]: filename }));
    }
  };

  const computeSeverity = (milk: string | null, udder: string[]): "grave" | "medio" | "leve" => {
    const u = new Set(udder);
    if (milk === "com sangue" || u.has("com ferida")) return "grave";
    const redFlags = ["inchado", "quente", "endurecido", "dolorido"];
    const countRed = redFlags.filter((k) => u.has(k)).length;
    if (milk === "com grumos" || countRed >= 2) return "medio";
    return "leve";
  };

  const handleNext = () => {
    if (prePhase) {
      if (preStepIndex === 0) {
        setPreStepIndex(1);
      } else {
        setPrePhase(false);
        setStepIndex(0);
      }
      return;
    }
    if (stepIndex < CATEGORY_FLOW.length - 1) {
      setStepIndex((i) => i + 1);
    } else {
      const severity = computeSeverity(milkCondition, udderConditions);
      const payload = {
        ...(initialData || {}),
        mastitis: {
          ...(initialData?.mastitis || {}),
          plateImageFile: initialData?.mastitis?.plateImageFile,
          collectionDate: initialData?.mastitis?.collectionDate,
          selectionsPrimary: {
            staphylococcus: selectedPrimary["ImagemCromogenicoStaphylococcus"] || null,
            geral: selectedPrimary["ImagemCromogenicoGeral"] || null,
            streptococcus: selectedPrimary["ImagemCromogenicoStreptococcus"] || null,
          },
          selectionsSecondary: {
            staphylococcus: selectedSecondary["ImagemCromogenicoStaphylococcus"] || null,
            geral: selectedSecondary["ImagemCromogenicoGeral"] || null,
            streptococcus: selectedSecondary["ImagemCromogenicoStreptococcus"] || null,
          },
          clinical: {
            milkCondition,
            udderConditions,
            severity,
          },
        },
      };
      updateContent("mastitisTestStep3", payload);
    }
  };

  const handleBack = () => {
    if (prePhase) {
      if (preStepIndex === 1) {
        setPreStepIndex(0);
        return;
      }
      updateContent("mastitisTestStep1", initialData);
      return;
    }
    if (stepIndex > 0) {
      setStepIndex((i) => Math.max(0, i - 1));
      return;
    }
    setPrePhase(true);
    setPreStepIndex(1);
  };

  const isPrimarySelectedForCurrent = Boolean(selectedPrimary[currentCategory.key]);

  const keysUpTo = (idx: number): CategoryKey[] => CATEGORY_FLOW.slice(0, idx + 1).map((c) => c.key);
  const matchForUpTo = (sel?: { [K in CategoryKey]?: string } | null, untilIndex = stepIndex) => {
    if (!sel) return null;
    const keys = keysUpTo(untilIndex);
    const activeKeys = keys.filter((k) => Boolean(sel[k]));
    if (activeKeys.length === 0) return null;
    let best: any = null;
    let bestScore = -1;
    for (const item of results) {
      let score = 0;
      for (const k of activeKeys) {
        if (item?.[k] === sel[k]) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    return best;
  };

  const partialPrimary = useMemo(() => matchForUpTo(selectedPrimary, stepIndex), [selectedPrimary, stepIndex, results]);

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {prePhase ? `Triagem clínica ${preStepIndex + 1}/2` : `Seleção de imagens ${stepIndex + 1}/${CATEGORY_FLOW.length}`}
      </Typography>

      {prePhase ? (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {preStepIndex === 0 ? "Como está o leite?" : "Como está a úbere?"}
          </Typography>
          <Grid container spacing={1.5}>
            {preStepIndex === 0 ? (
              [
                { key: "normal", label: "Normal", icon: <LocalDrink /> },
                { key: "com grumos", label: "Com grumos", icon: <Grain /> },
                { key: "aguado", label: "Aguado", icon: <WaterDrop /> },
                { key: "com sangue", label: "Com sangue", icon: <Bloodtype /> },
              ].map((opt) => (
                <Grid key={opt.key} size={{ xs: 6 }}>
                  <Paper
                    onClick={() => setMilkCondition(opt.key)}
                    elevation={0}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: milkCondition === opt.key ? "primary.main" : "divider",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {opt.icon}
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {opt.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))
            ) : (
              [
                { key: "inchado", label: "Inchado", icon: <Whatshot /> },
                { key: "quente", label: "Quente", icon: <Whatshot /> },
                { key: "endurecido", label: "Endurecido", icon: <PanTool /> },
                { key: "dolorido", label: "Dolorido", icon: <Healing /> },
                { key: "frio", label: "Frio", icon: <AcUnit /> },
                { key: "com ferida", label: "Com ferida", icon: <Healing /> },
              ].map((opt) => {
                const active = udderConditions.includes(opt.key);
                return (
                  <Grid key={opt.key} size={{ xs: 6 }}>
                    <Paper
                      onClick={() => {
                        setUdderConditions((prev) =>
                          prev.includes(opt.key) ? prev.filter((p) => p !== opt.key) : [...prev, opt.key]
                        );
                      }}
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: active ? "primary.main" : "divider",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {opt.icon}
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {opt.label}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })
            )}
          </Grid>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{currentCategory.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {currentCategory.hint}
          </Typography>
          <Grid container spacing={1.5}>
            {imagesForCurrent.map((filename) => {
              const isPrimary = selectedPrimary[currentCategory.key] === filename;
              const isSecondary = selectedSecondary[currentCategory.key] === filename;
              return (
                <Grid key={filename} size={{ xs: 6 }}>
                  <Box
                    onClick={() => handleSelect(filename)}
                    sx={{
                      border: "2px solid",
                      borderColor: isPrimary ? "primary.main" : isSecondary ? "info.main" : "divider",
                      borderRadius: 2,
                      p: 1,
                      position: "relative",
                      minHeight: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {(isPrimary || isSecondary) && (
                      <Chip
                        label={isPrimary ? "1" : "2"}
                        size="small"
                        color={isPrimary ? "primary" : "info"}
                        sx={{ position: "absolute", top: 6, left: 6, height: 20 }}
                      />
                    )}
                    <Box
                      component="img"
                      src={`/mastitis-imgs/${filename}`}
                      alt={filename}
                      sx={{ maxWidth: "100%", maxHeight: 90, objectFit: "contain", borderRadius: 1 }}
                    />
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">
            Análise parcial: {partialPrimary?.Microrganismo || "Selecione uma imagem"}
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button variant="outlined" startIcon={<NavigateBefore />} onClick={handleBack}>
          Voltar
        </Button>
        <Button
          variant="contained"
          endIcon={<NavigateNext />}
          onClick={handleNext}
          disabled={prePhase ? (preStepIndex === 0 ? !milkCondition : udderConditions.length === 0) : !isPrimarySelectedForCurrent}
        >
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

const MobileMastitisTestStep3: React.FC<{ initialData?: any }> = ({ initialData }) => {
  const { updateContent, restorePreviousContent } = useDashboard();
  const { authState } = useAuth();
  const { selectedOrganization, activeSessionId, sessions } = useChatContext();

  const selectionsPrimary = initialData?.mastitis?.selectionsPrimary || {};
  const selectionsSecondary = initialData?.mastitis?.selectionsSecondary || null;
  const clinical = initialData?.mastitis?.clinical || null;
  const teats: string[] = initialData?.mastitis?.teats || [];
  const plateImageFile = initialData?.mastitis?.plateImageFile;
  const [finalNotes, setFinalNotes] = useState<string>(initialData?.mastitis?.finalNotes || "");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sendOutcome, setSendOutcome] = useState<"idle" | "success" | "error">("idle");
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultType, setResultType] = useState<"positive" | "negative" | "error" | "idle">("idle");
  const [resultModalOpen, setResultModalOpen] = useState(false);

  const results = (infoMastitis as any)?.ResultsMastitis || [];
  const plateImageUrl = useMemo(() => {
    try {
      return plateImageFile ? URL.createObjectURL(plateImageFile) : null;
    } catch {
      return null;
    }
  }, [plateImageFile]);

  const matchFor = (sel?: any) => {
    if (!sel) return null;
    const staph = sel?.staphylococcus;
    const geral = sel?.geral;
    const strep = sel?.streptococcus;
    const pairs: Array<{ key: string; val?: string | null }> = [
      { key: "ImagemCromogenicoStaphylococcus", val: staph },
      { key: "ImagemCromogenicoGeral", val: geral },
      { key: "ImagemCromogenicoStreptococcus", val: strep },
    ];
    const active = pairs.filter((p) => Boolean(p.val));
    if (active.length === 0) return null;
    let best: any = null;
    let bestScore = -1;
    for (const item of results) {
      let score = 0;
      for (const p of active) {
        if ((item as any)?.[p.key] === p.val) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
    return best;
  };

  const matchPrimary = useMemo(() => matchFor(selectionsPrimary), [results, selectionsPrimary]);
  const matchSecondary = useMemo(() => matchFor(selectionsSecondary), [results, selectionsSecondary]);
  const treatmentPlan = useMemo(() => getTreatmentByOrganism(matchPrimary?.Microrganismo || ""), [matchPrimary]);

  const handleNotesBlur = () => {
    updateContent("mastitisTestStep3", {
      ...(initialData || {}),
      mastitis: {
        ...(initialData?.mastitis || {}),
        finalNotes: finalNotes?.trim() || null,
      },
    });
  };

  const deriveResultType = (data: any): "positive" | "negative" => {
    const examResult = data?.examResult || null;
    const sp = data?.selectionsPrimary || {};
    const ss = data?.selectionsSecondary || {};
    const hasOrganism = Boolean(sp?.staphylococcus || sp?.streptococcus || sp?.geral || ss?.staphylococcus || ss?.streptococcus || ss?.geral);
    if (examResult && typeof examResult === "string") {
      return "positive";
    }
    return hasOrganism ? "positive" : "negative";
  };

  const handleSubmitTest = async () => {
    const session = sessions.find((s) => s.session_id === activeSessionId);
    const activeOrganization = selectedOrganization;
    const ownerId = (activeOrganization as any)?.owner_id || authState.profileId || authState.user?.id;
    const businessUnitId = session?.organization_id || (activeOrganization as any)?._key || activeOrganization?.id;

    if (!ownerId || !businessUnitId || !activeSessionId) {
      setSubmitError("Dados de usuário ou organização não encontrados");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSendOutcome("idle");
    setResultMessage(null);
    setResultType("idle");
    setResultModalOpen(false);

    try {
      const testData: MastitisTestData = {
        animalId: initialData?.mastitis?.animalId,
        teats,
        plateImageFile,
        collectionDate: initialData?.mastitis?.collectionDate,
        selectionsPrimary,
        selectionsSecondary,
        clinical,
        finalNotes: finalNotes?.trim() || undefined,
      };

      const result = await MastitisTestService.sendMastitisTest(
        testData,
        ownerId,
        businessUnitId,
        activeSessionId
      );

      if (result.success) {
        const rType = deriveResultType(testData);
        setSendOutcome("success");
        setResultType(rType);
        setResultMessage(rType === "positive" ? "Indícios de mastite." : "Sem evidências relevantes de mastite.");
      } else {
        const errMsg = result.error || "Erro ao enviar teste";
        setSubmitError(errMsg);
        setSendOutcome("error");
        setResultType("error");
        setResultMessage(errMsg);
      }
    } catch {
      const errMsg = "Erro inesperado ao enviar teste";
      setSubmitError(errMsg);
      setSendOutcome("error");
      setResultType("error");
      setResultMessage(errMsg);
    } finally {
      setIsSubmitting(false);
      setResultModalOpen(true);
    }
  };

  const handleExit = () => {
    setResultModalOpen(false);
    restorePreviousContent();
  };

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Resumo do Teste</Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">Animal: {initialData?.mastitis?.animalId || "Não informado"}</Typography>
          <Typography variant="body2" color="text.secondary">Data da coleta: {formatDate(initialData?.mastitis?.collectionDate)}</Typography>
          <Typography variant="body2" color="text.secondary">Tetos: {teats.length ? teats.join(", ") : "Não informado"}</Typography>
          <Typography variant="body2" color="text.secondary">Leite: {clinical?.milkCondition || "-"}</Typography>
          <Typography variant="body2" color="text.secondary">Úbere: {clinical?.udderConditions?.length ? clinical.udderConditions.join(", ") : "-"}</Typography>
          <Typography variant="body2" color="text.secondary">Severidade: {clinical?.severity || "-"}</Typography>
        </Stack>
      </Paper>

      {plateImageUrl && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Imagem enviada</Typography>
          <Box component="img" src={plateImageUrl} alt="Placa enviada" sx={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 2 }} />
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Análise sugerida</Typography>
        <Typography variant="body2" color="text.secondary">
          Primário: {matchPrimary?.Microrganismo || "Não definido"}
        </Typography>
        {matchSecondary?.Microrganismo && (
          <Typography variant="body2" color="text.secondary">
            Secundário: {matchSecondary?.Microrganismo}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Esta análise é gerada por IA com base nas imagens e informações selecionadas. Não constitui diagnóstico e não substitui avaliação clínica. Consulte sempre um(a) médico(a) veterinário(a).
        </Typography>
      </Paper>

      {treatmentPlan && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Conduta sugerida</Typography>
          {treatmentPlan.situacao && <Typography variant="body2" color="text.secondary">Situação: {treatmentPlan.situacao}</Typography>}
          {treatmentPlan.condutaPrincipal && <Typography variant="body2" color="text.secondary">Conduta: {treatmentPlan.condutaPrincipal}</Typography>}
          {treatmentPlan.suporte && <Typography variant="body2" color="text.secondary">Suporte: {treatmentPlan.suporte}</Typography>}
          {treatmentPlan.antibioticos && <Typography variant="body2" color="text.secondary">Antibióticos: {treatmentPlan.antibioticos}</Typography>}
          {treatmentPlan.aines && <Typography variant="body2" color="text.secondary">AINEs: {treatmentPlan.aines}</Typography>}
          {Array.isArray(AINES_RECOMENDADOS) && AINES_RECOMENDADOS.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              AINEs recomendados: {AINES_RECOMENDADOS.join(", ")}
            </Typography>
          )}
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Observações</Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={finalNotes}
          onChange={(e) => setFinalNotes(e.target.value)}
          onBlur={handleNotesBlur}
          placeholder="Adicione observações finais"
        />
      </Paper>

      {submitError && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorOutline color="error" />
          <Typography variant="body2" color="error">{submitError}</Typography>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleSubmitTest}
        disabled={isSubmitting}
        startIcon={isSubmitting ? <CircularProgress size={18} /> : <Send />}
      >
        {isSubmitting ? "Enviando..." : "Enviar teste"}
      </Button>

      <Dialog open={resultModalOpen} onClose={() => setResultModalOpen(false)} fullWidth>
        <DialogTitle>Resultado do teste</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 2 }}>
            {sendOutcome === "success" ? <CheckCircle color="success" sx={{ fontSize: 48 }} /> : <ErrorOutline color="error" sx={{ fontSize: 48 }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {resultMessage || "Resultado indisponível"}
            </Typography>
            {resultType === "positive" && (
              <Typography variant="body2" color="text.secondary">
                Avalie a conduta sugerida com apoio veterinário e acompanhe a evolução do animal.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              Informações geradas por IA com base nas imagens selecionadas. Não substituem a avaliação de um(a) médico(a) veterinário(a).
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
              <Button variant="contained" onClick={() => updateContent("mastitisIntro")}>
                Novo teste
              </Button>
              <Button variant="outlined" onClick={handleExit}>
                Sair
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const MobileExamList: React.FC<{ examData: any }> = ({ examData }) => {
  const { updateContent } = useDashboard();
  const exams = useMemo(() => {
    if (!examData) return [];
    if (Array.isArray(examData)) return examData;
    if (examData?.exams) return examData.exams;
    if (examData?.data) return examData.data;
    return [];
  }, [examData]);

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Exames</Typography>
      {examData?.resumo && (
        <Typography variant="body2" color="text.secondary">{examData.resumo}</Typography>
      )}
      <Stack spacing={1.5}>
        {exams.map((exam: any) => (
          <Paper key={exam._id || exam._key} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {exam.cattle_tag || "Animal"} · {formatDate(exam.handling_date || exam.handling_details?.exam_date)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tipo: {exam.handling_details?.exam_type || "Exame"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Resultado: {exam.handling_details?.results?.exam_result || exam.handling_details?.exam_result || "-"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button variant="outlined" size="small" onClick={() => updateContent("exam-list", { examData: { exams: [exam] } })}>
                Ver detalhes
              </Button>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

const MobileExamDetail: React.FC<{ examData: any }> = ({ examData }) => {
  const exams = useMemo(() => {
    if (!examData) return [];
    if (Array.isArray(examData)) return examData;
    if (examData?.exams) return examData.exams;
    if (examData?.data) return examData.data;
    return [];
  }, [examData]);
  const exam = exams[0];
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState<string | null>(null);
  const [tempAccessLoadingKey, setTempAccessLoadingKey] = useState<string | null>(null);
  const [tempAccessError, setTempAccessError] = useState<string | null>(null);

  const attachments = normalizeAttachments(exam?.handling_details?.attachments);
  const firstImage = attachments.find((a: any) => a?.type === "image" && a?.url);

  const fetchTempAccessAndOpen = async (objectKey: string) => {
    try {
      setTempAccessLoadingKey(objectKey);
      setTempAccessError(null);
      const resp = await storageService.getTempAccess({
        bucket: (import.meta as any)?.env?.VITE_S3_BUCKET_EXAMES || "exames",
        objectKey,
        request: {
          expiresInMinutes: 300,
          maxAccess: 100,
          description: "Preview",
        },
      });
      if (resp?.accessUrl) {
        setImageModalSrc(resp.accessUrl);
        setImageModalOpen(true);
      } else {
        setTempAccessError("Não foi possível obter acesso temporário.");
      }
    } catch {
      setTempAccessError("Erro ao gerar acesso temporário.");
    } finally {
      setTempAccessLoadingKey(null);
    }
  };

  if (!exam) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">Nenhum exame encontrado.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalhes do exame</Typography>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {exam.cattle_tag || "Animal"} · {formatDate(exam.handling_date || exam.handling_details?.exam_date)}
        </Typography>
        <Typography variant="body2" color="text.secondary">Tipo: {exam.handling_details?.exam_type || "-"}</Typography>
        <Typography variant="body2" color="text.secondary">Placa: {exam.handling_details?.plate_id || "-"}</Typography>
        <Typography variant="body2" color="text.secondary">Coleta: {formatDate(exam.handling_details?.collection_date)}</Typography>
        <Typography variant="body2" color="text.secondary">Resultado: {exam.handling_details?.results?.exam_result || exam.handling_details?.exam_result || "-"}</Typography>
      </Paper>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Detalhes clínicos</Typography>
        <Typography variant="body2" color="text.secondary">Leite: {exam.handling_details?.results?.milkAspect?.join(", ") || "-"}</Typography>
        <Typography variant="body2" color="text.secondary">Úbere: {exam.handling_details?.results?.udderSigns?.join(", ") || "-"}</Typography>
        <Typography variant="body2" color="text.secondary">Tetos: {exam.handling_details?.results?.teatsAffected?.join(", ") || "-"}</Typography>
      </Paper>
      {(exam.handling_details?.results?.mainTreatment || exam.handling_details?.results?.supportiveTreatment || exam.handling_details?.results?.recommendedAntibiotics) && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Tratamento</Typography>
          {exam.handling_details?.results?.situation && <Typography variant="body2" color="text.secondary">Situação: {exam.handling_details.results.situation}</Typography>}
          {exam.handling_details?.results?.mainTreatment && <Typography variant="body2" color="text.secondary">Conduta: {exam.handling_details.results.mainTreatment}</Typography>}
          {exam.handling_details?.results?.supportiveTreatment && <Typography variant="body2" color="text.secondary">Suporte: {exam.handling_details.results.supportiveTreatment}</Typography>}
          {exam.handling_details?.results?.recommendedAntibiotics && <Typography variant="body2" color="text.secondary">Antibióticos: {exam.handling_details.results.recommendedAntibiotics}</Typography>}
        </Paper>
      )}
      {firstImage?.url && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Imagem da placa</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => fetchTempAccessAndOpen(firstImage.url)}
            disabled={tempAccessLoadingKey === firstImage.url}
          >
            {tempAccessLoadingKey === firstImage.url ? "Carregando..." : "Ver imagem"}
          </Button>
          {tempAccessError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {tempAccessError}
            </Typography>
          )}
        </Paper>
      )}

      <Dialog open={imageModalOpen} onClose={() => setImageModalOpen(false)} fullWidth>
        <DialogTitle>Imagem do exame</DialogTitle>
        <DialogContent>
          {imageModalSrc && (
            <Box component="img" src={imageModalSrc} alt="Imagem do exame" sx={{ width: "100%", borderRadius: 2 }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export const MobileExamsFlow: React.FC = () => {
  const { contentView, contentData } = useDashboard();

  if (contentView === "examsHub") return <MobileExamsHub />;
  if (contentView === "mastitisIntro") return <MobileMastitisIntro />;
  if (contentView === "mastitisTeatStep") return <MobileMastitisTeatStep initialData={contentData} />;
  if (contentView === "mastitisAnimalIdStep") return <MobileMastitisAnimalIdStep initialData={contentData} />;
  if (contentView === "mastitisTestStep1") return <MobileMastitisTestStep1 initialData={contentData} />;
  if (contentView === "mastitisTestStep2") return <MobileMastitisTestStep2 initialData={contentData} />;
  if (contentView === "mastitisTestStep3") return <MobileMastitisTestStep3 initialData={contentData} />;
  if (contentView === "exam-detail") return <MobileExamList examData={contentData?.examData} />;
  if (contentView === "exam-list") return <MobileExamDetail examData={contentData?.examData} />;

  return null;
};
